import { useEffect, useState } from "react";
import { BookUser, Download } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, API } from "../lib/api";
import { formatPhone } from "../lib/phone";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";

const FIELDS = [
  ["account_duration", "Account duration"],
  ["terms_extended", "Terms"],
  ["highest_balance", "Highest balance"],
  ["current_balance", "Current balance"],
  ["past_due", "Past due"],
];

export const RegistryReportButton = () => {
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!open) return;
    setEntries(null);
    api
      .get("/admin/registry")
      .then(({ data }) => setEntries(data))
      .catch((e) => toast.error(errMsg(e)));
  }, [open]);

  const download = async () => {
    try {
      const { data } = await api.get("/admin/registry.csv", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "ConfirmTerms Reference Registry.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Registry exported");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const q = query.trim().toLowerCase();
  const shown = (entries || []).filter((e) =>
    q
      ? [e.company_name, e.contact_name, e.email].some((v) => (v || "").toLowerCase().includes(q))
      : true
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          data-testid="registry-report-button"
          className="h-11 rounded-full border-slate-300 px-6 font-semibold transition-colors duration-200 hover:bg-slate-100"
        >
          <BookUser className="mr-2 h-4 w-4" /> Reference registry
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-extrabold tracking-tight">
            Reference registry — opted-in trade references
          </DialogTitle>
          <DialogDescription>
            Trade references that agreed to be listed as a Generic Trade Reference. Search, review
            their latest answers, or export the whole registry as CSV.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search company, contact or email…"
            data-testid="registry-search-input"
            className="h-10 max-w-xs border-slate-300 focus-visible:ring-blue-600"
          />
          <div className="flex items-center gap-3">
            <span className="text-xs font-semibold text-slate-500" data-testid="registry-count">
              {entries ? `${entries.length} on file` : "Loading…"}
            </span>
            <Button
              size="sm"
              onClick={download}
              data-testid="registry-csv-button"
              className="rounded-full bg-slate-900 px-5 font-semibold hover:bg-slate-800"
            >
              <Download className="mr-1.5 h-3.5 w-3.5" /> Export CSV
            </Button>
          </div>
        </div>

        {entries && entries.length === 0 ? (
          <p
            className="rounded-md border border-dashed border-slate-300 p-10 text-center text-sm text-slate-500"
            data-testid="registry-empty"
          >
            No references have opted into the registry yet.
          </p>
        ) : (
          <div className="max-h-[26rem] space-y-3 overflow-y-auto pr-1">
            {shown.map((e) => (
              <div
                key={e.email}
                data-testid="registry-row"
                className="rounded-md border border-slate-200 bg-white p-5"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="font-display text-base font-bold text-slate-900">
                      {e.company_name}
                    </div>
                    <div className="text-sm text-slate-600">
                      {e.contact_name} · {e.email}
                    </div>
                  </div>
                  <span className="rounded-full bg-teal-50 px-3 py-1 text-xs font-semibold text-[#1f4e5a]">
                    {e.submissions} submission{e.submissions === 1 ? "" : "s"}
                  </span>
                </div>
                <dl className="mt-4 grid gap-3 border-t border-slate-100 pt-4 sm:grid-cols-3">
                  {FIELDS.filter(([k]) => e.responses?.[k]).map(([k, label]) => (
                    <div key={k}>
                      <dt className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                        {label}
                      </dt>
                      <dd className="mt-0.5 text-sm text-slate-900">
                        {k === "phone" ? formatPhone(e.responses[k]) : e.responses[k]}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
